const GAP = 10, FIELD_H = 22, CAP_H = 30, PAD = 8, GROUP_H = 28;
const CHECKISH = new Set(['check', 'check3', 'radio']);
export class CardView {
    constructor(grid, root) {
        this.grid = grid;
        /** 표시 항목 (0 이상 = 물리 행, 음수 = 그룹 머리행) */
        this.items = [];
        this.cur = 0;
        this.perLine = 1;
        this.cardH = 100;
        this.raf = 0;
        /** 줄마다 첫 항목 번호 (마지막에 items.length 하나 더) · 줄 높이 · 줄 위치(마지막 = 전체 높이) */
        this.lineStart = new Int32Array(1);
        this.lineH = new Float64Array(0);
        this.lineTop = new Float64Array(1);
        /** 자동 높이: 물리 행 → 잰 카드 높이 (카드 너비·컬럼이 바뀌면 비움) */
        this.rowH = new Map();
        this.hKey = '';
        this.edit = null;
        this.ro = null;
        this.el = document.createElement('div');
        this.el.className = 'og-cards';
        this.el.tabIndex = 0;
        this.el.setAttribute('role', 'listbox');
        this.el.setAttribute('aria-label', grid.t('카드 보기'));
        this.inner = document.createElement('div');
        this.inner.className = 'og-cardsi';
        this.el.appendChild(this.inner);
        root.appendChild(this.el);
        const fr = grid.getFocusedRow();
        this.el.addEventListener('scroll', () => this.schedule());
        this.el.addEventListener('keydown', e => this.onKey(e));
        this.el.addEventListener('mousedown', e => {
            const t = e.target;
            const card = t.closest('.og-card,.og-cardg');
            if (!card || t.tagName === 'INPUT' && !t.classList.contains('og-cfk') || t.tagName === 'SELECT')
                return;
            this.select(+card.dataset.i);
        });
        this.el.addEventListener('click', e => {
            const t = e.target;
            const gh = t.closest('.og-cardg');
            if (gh) {
                this.toggleGroupAt(+gh.dataset.i);
                return;
            }
            if (t.classList.contains('og-cfk')) { // 체크·라디오: 그리드 규칙대로 바꿈 (라디오 그룹·실행 취소)
                e.preventDefault();
                const f = t.closest('.og-cf'), card = t.closest('.og-card');
                const row = this.items[+card.dataset.i];
                if (this.edit && this.edit.input !== t && !this.commit())
                    return;
                if (this.edit && this.edit.input !== t)
                    this.endEdit(false);
                if (row >= 0 && this.grid.toggleCell(row, f.dataset.col)) {
                    if (this.edit?.input === t)
                        setTimeout(() => this.syncCheck(t, row, f.dataset.col));
                    else
                        this.refresh();
                } // 취소한 클릭은 브라우저가 체크를 되돌리므로 그 뒤에 맞춤
            }
        });
        this.el.addEventListener('dblclick', e => {
            const f = e.target.closest('.og-cf'), card = f?.closest('.og-card');
            if (f && card && !e.target.classList.contains('og-cfk')) {
                this.select(+card.dataset.i);
                this.beginEdit(f.dataset.col);
            }
        });
        this.ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { if (!this.edit)
            this.schedule(); }) : null;
        this.ro?.observe(this.el);
        this.refresh();
        if (fr >= 0) {
            const i = Array.prototype.indexOf.call(this.items, fr);
            if (i >= 0)
                this.select(i);
        }
    }
    destroy() { cancelAnimationFrame(this.raf); this.ro?.disconnect(); this.el.remove(); }
    focus() { this.el.focus({ preventScroll: true }); }
    /** 현재 카드의 물리 행 (그룹 머리행이 선택돼 있으면 -1) */
    currentRow() { const n = this.items.length; return n ? this.items[Math.min(this.cur, n - 1)] : -1; }
    currentIndex() { return this.cur; }
    fields() {
        const cols = this.grid.visibleColumns();
        const capId = this.grid.opts.cardCaption && cols.some(c => c.id === this.grid.opts.cardCaption) ? this.grid.opts.cardCaption : cols[0]?.id;
        return { cap: cols.find(c => c.id === capId), list: cols.filter(c => c.id !== capId) };
    }
    get auto() { return !!this.grid.opts.cardAutoHeight; }
    /** 데이터·컬럼이 바뀌면 다시 (그리드 afterView 에서 부름) */
    refresh() {
        if (this.edit)
            return; // 편집 중에는 다시 그리지 않음 (확정 뒤 다시)
        // 그룹 푸터·소계 줄은 빼고 머리행·데이터 행만
        const d = this.grid.getDisplayItems();
        let items = d;
        for (let i = 0; i < d.length; i++)
            if (d[i] < 0 && !this.grid.groupInfo(d[i])) {
                items = Array.prototype.filter.call(d, (v) => v >= 0 || !!this.grid.groupInfo(v));
                break;
            }
        this.items = items;
        if (this.cur >= items.length)
            this.cur = Math.max(0, items.length - 1);
        this.draw();
    }
    schedule() { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.draw()); }
    /** 줄 나누기: 그룹 머리행은 한 줄 전체, 카드는 한 줄에 perLine 장 (그룹이 바뀌면 새 줄) */
    buildLines() {
        const it = this.items, n = it.length, pl = this.perLine, H0 = this.cardH;
        const starts = [];
        let k = pl;
        for (let i = 0; i < n; i++) {
            if (it[i] < 0) {
                starts.push(i);
                k = pl;
                continue;
            }
            if (k >= pl) {
                starts.push(i);
                k = 0;
            }
            k++;
        }
        starts.push(n);
        const L = starts.length - 1, ls = Int32Array.from(starts), lh = new Float64Array(L), useH = this.auto && this.rowH.size > 0;
        for (let l = 0; l < L; l++) {
            if (it[ls[l]] < 0) {
                lh[l] = GROUP_H;
                continue;
            }
            let h = H0;
            if (useH)
                for (let i = ls[l]; i < ls[l + 1]; i++) {
                    const m = this.rowH.get(it[i]);
                    if (m != null && m > h)
                        h = m;
                }
            lh[l] = h;
        }
        this.lineStart = ls;
        this.lineH = lh;
        this.lineTop = new Float64Array(L + 1);
        this.retop(0);
    }
    retop(from) {
        const t = this.lineTop, h = this.lineH;
        if (from === 0)
            t[0] = GAP;
        for (let l = Math.max(0, from); l < h.length; l++)
            t[l + 1] = t[l] + h[l] + GAP;
    }
    /** 항목 i 가 있는 줄 */
    lineOf(i) {
        const s = this.lineStart;
        let lo = 0, hi = s.length - 2;
        while (lo < hi) {
            const m = (lo + hi + 1) >> 1;
            if (s[m] <= i)
                lo = m;
            else
                hi = m - 1;
        }
        return Math.max(0, lo);
    }
    /** 위치 y 에 걸친 줄 */
    lineAt(y) {
        const t = this.lineTop;
        let lo = 0, hi = t.length - 2;
        while (lo < hi) {
            const m = (lo + hi + 1) >> 1;
            if (t[m] <= y)
                lo = m;
            else
                hi = m - 1;
        }
        return Math.max(0, lo);
    }
    metrics() {
        const cw = this.grid.opts.cardWidth ?? 240;
        const W = this.el.clientWidth || 600;
        this.perLine = Math.max(1, Math.floor((W - GAP) / (cw + GAP)));
        const { list } = this.fields();
        this.cardH = CAP_H + list.length * FIELD_H + PAD;
        const key = cw + '|' + list.map(f => f.id).join();
        if (key !== this.hKey) {
            this.hKey = key;
            this.rowH.clear();
        }
        this.el.classList.toggle('og-cauto', this.auto);
        this.buildLines();
        return cw;
    }
    draw(pass = 0) {
        if (this.edit)
            return; // 편집 중 스크롤·크기 변경으로 다시 그리면 입력칸이 사라짐 — 확정·취소 뒤 다시
        const cw = pass ? this.grid.opts.cardWidth ?? 240 : this.metrics(), L = this.lineH.length;
        this.inner.style.height = this.lineTop[L] + 'px';
        const top = this.el.scrollTop, H = this.el.clientHeight || 400;
        const l0 = Math.max(0, this.lineAt(top) - 1), l1 = Math.min(L - 1, this.lineAt(top + H) + 1);
        const { cap, list } = this.fields();
        const g = this.grid, auto = this.auto, fullW = this.perLine * (cw + GAP) - GAP;
        const frag = document.createDocumentFragment(), drawn = [];
        for (let l = l0; l <= l1; l++)
            for (let i = this.lineStart[l], k = 0; i < this.lineStart[l + 1]; i++, k++) {
                const item = this.items[i], y = this.lineTop[l];
                if (item < 0) {
                    const gi = g.groupInfo(item);
                    if (!gi)
                        continue;
                    const gh = document.createElement('div');
                    gh.className = 'og-cardg' + (i === this.cur ? ' og-cur' : '');
                    gh.dataset.i = String(i);
                    gh.setAttribute('role', 'option');
                    gh.setAttribute('aria-selected', String(i === this.cur));
                    gh.setAttribute('aria-expanded', String(!gi.collapsed));
                    gh.style.cssText = `left:${GAP}px;top:${y}px;width:${fullW}px;height:${GROUP_H}px;padding-left:${8 + gi.level * 18}px`;
                    gh.textContent = `${gi.collapsed ? '▸' : '▾'} ${gi.text}`;
                    frag.appendChild(gh);
                    continue;
                }
                const row = item;
                const card = document.createElement('div');
                card.className = 'og-card' + (i === this.cur ? ' og-cur' : '');
                card.dataset.i = String(i);
                card.setAttribute('role', 'option');
                card.setAttribute('aria-selected', String(i === this.cur));
                card.style.left = GAP + k * (cw + GAP) + 'px';
                card.style.top = y + 'px';
                card.style.width = cw + 'px';
                if (auto)
                    card.style.minHeight = this.cardH + 'px';
                else
                    card.style.height = this.cardH + 'px';
                const h = document.createElement('div');
                h.className = 'og-cardh';
                h.textContent = cap ? g.cellText(row, cap.id) : String(i + 1);
                if (cap)
                    h.title = cap.header;
                card.appendChild(h);
                for (const f of list)
                    card.appendChild(this.fieldEl(row, f));
                frag.appendChild(card);
                drawn.push({ l, el: card, row });
            }
        this.inner.textContent = '';
        this.inner.appendChild(frag);
        // 자동 높이: 그린 카드를 재서 줄 높이가 바뀌면 그 줄부터 위치를 다시 (한 번 더 그림)
        if (auto && drawn.length && pass < 2) {
            const lh = new Map();
            for (const d of drawn) {
                const m = d.el.offsetHeight;
                if (m > 0) {
                    this.rowH.set(d.row, m);
                    lh.set(d.l, Math.max(lh.get(d.l) ?? this.cardH, m));
                }
            }
            let from = -1;
            for (const [l, h] of lh)
                if (Math.abs(this.lineH[l] - h) > 0.5) {
                    this.lineH[l] = h;
                    if (from < 0 || l < from)
                        from = l;
                }
            if (from >= 0) {
                this.retop(from);
                this.draw(pass + 1);
            }
        }
    }
    fieldEl(row, f) {
        const g = this.grid;
        const fe = document.createElement('div');
        fe.className = 'og-cf';
        fe.dataset.col = f.id;
        const lb = document.createElement('span');
        lb.className = 'og-cfl';
        lb.textContent = f.header;
        const kind = g.editorKind(row, f.id);
        fe.appendChild(lb);
        const ck = kind && CHECKISH.has(kind) ? kind : this.checkKindOf(row, f.id);
        if (ck) { // 체크·라디오: 바로 누르는 칸
            const k = document.createElement('input');
            k.type = ck === 'radio' ? 'radio' : 'checkbox';
            k.className = 'og-cfk';
            k.tabIndex = -1;
            k.disabled = !kind;
            k.setAttribute('aria-label', f.header);
            this.syncCheck(k, row, f.id);
            const v = document.createElement('span');
            v.className = 'og-cfv og-cfc';
            v.appendChild(k);
            fe.appendChild(v);
            if (kind)
                fe.classList.add('og-cfe');
            return fe;
        }
        const v = document.createElement('span');
        v.className = 'og-cfv' + (f.align === 'r' ? ' og-num' : '');
        v.textContent = g.cellText(row, f.id);
        if (kind)
            fe.classList.add('og-cfe');
        fe.appendChild(v);
        return fe;
    }
    /** 편집할 수 없어도 체크 컬럼이면 체크 모양으로 (컬럼 editor 가 check·radio) */
    checkKindOf(row, col) {
        const def = this.grid.opts.columns.find(c => c.id === col);
        const e = def?.editor;
        return typeof e === 'string' && CHECKISH.has(e) && row >= 0 ? e : null;
    }
    syncCheck(k, row, col) {
        const on = this.grid.isCellChecked(row, col);
        k.checked = on === true;
        k.indeterminate = on === null;
    }
    select(i, scroll = true) {
        if (!this.items.length)
            return;
        this.cur = Math.max(0, Math.min(this.items.length - 1, i));
        const l = this.lineOf(this.cur);
        if (scroll && l < this.lineH.length) {
            const y = this.lineTop[l], h = this.lineH[l];
            if (y < this.el.scrollTop)
                this.el.scrollTop = y - GAP;
            else if (y + h > this.el.scrollTop + this.el.clientHeight)
                this.el.scrollTop = y + h + GAP - this.el.clientHeight;
        }
        // 이미 그려진 카드면 표시만 바꿈 (다시 그리면 더블클릭 대상이 사라짐). 스크롤로 새 줄이 필요하면 scroll 이벤트가 다시 그림
        const now = this.cardEl(this.cur);
        if (now) {
            this.inner.querySelectorAll('.og-cur').forEach(c => { c.classList.remove('og-cur'); c.setAttribute('aria-selected', 'false'); });
            now.classList.add('og-cur');
            now.setAttribute('aria-selected', 'true');
        }
        else
            this.draw();
        const row = this.currentRow();
        if (row >= 0)
            this.grid.opts.onRowFocus?.({ row, col: '', value: null, grid: this.grid });
    }
    toggleGroupAt(i) {
        const item = this.items[i];
        if (item == null || item >= 0)
            return;
        this.cur = i;
        this.grid.toggleGroup(item); // 그리드가 다시 만들고 refresh 를 부름
        this.refresh();
        this.select(this.cur, false);
        this.focus();
    }
    cardEl(i) { return this.inner.querySelector(`[data-i="${i}"]`); }
    /** 현재 카드의 칸 편집 (col 생략 = 첫 편집 가능 칸) */
    beginEdit(col) {
        const row = this.currentRow();
        if (row < 0)
            return false;
        const g = this.grid;
        const list = this.fields().list.filter(f => g.editorKind(row, f.id) != null);
        const f = col ? list.find(x => x.id === col) : list[0];
        if (!f)
            return false;
        const fe = this.cardEl(this.cur)?.querySelector(`.og-cf[data-col="${CSS.escape(f.id)}"]`);
        if (!fe)
            return false;
        const kind = g.editorKind(row, f.id);
        let input;
        if (CHECKISH.has(kind)) { // 체크·라디오: 그 칸에 포커스, Space 로 바꿈
            input = fe.querySelector('.og-cfk');
            if (!input)
                return false;
            input.tabIndex = 0;
        }
        else if (kind === 'combo') { // 콤보: 목록에서 고름
            const sel = document.createElement('select');
            sel.className = 'og-cfi og-cfs';
            sel.setAttribute('aria-label', f.header);
            const cur = g.ds.get(row, f.id), items = g.cellItems(row, f.id);
            const opt = (value, label) => { const o = document.createElement('option'); o.value = value; o.textContent = label; return o; };
            sel.appendChild(opt('', ''));
            for (const it of items)
                sel.appendChild(opt(it.value, it.label || it.value));
            if (cur != null && cur !== '' && !items.some(it => it.value === String(cur)))
                sel.appendChild(opt(String(cur), String(cur)));
            sel.value = cur == null ? '' : String(cur);
            fe.querySelector('.og-cfv').replaceWith(sel);
            input = sel;
        }
        else {
            const inp = document.createElement('input');
            inp.className = 'og-cfi';
            inp.value = g.editText(row, f.id);
            inp.setAttribute('aria-label', f.header);
            fe.querySelector('.og-cfv').replaceWith(inp);
            input = inp;
        }
        this.edit = { row, col: f.id, input, kind };
        input.focus();
        if (input instanceof HTMLInputElement && input.type === 'text')
            input.select();
        const ie = input;
        ie.addEventListener('keydown', e => {
            e.stopPropagation();
            if (e.key === 'Escape') {
                e.preventDefault();
                this.endEdit();
            }
            else if (e.key === 'Enter') {
                e.preventDefault();
                if (this.commit())
                    this.endEdit();
            }
            else if (e.key === ' ' && CHECKISH.has(kind)) {
                e.preventDefault();
                if (g.toggleCell(row, f.id))
                    this.syncCheck(input, row, f.id);
            }
            else if (e.key === 'Tab') {
                e.preventDefault();
                const ids = list.map(x => x.id), j = ids.indexOf(f.id) + (e.shiftKey ? -1 : 1);
                if (!this.commit())
                    return;
                this.endEdit();
                if (j >= 0 && j < ids.length)
                    this.beginEdit(ids[j]);
            }
        });
        if (CHECKISH.has(kind))
            ie.addEventListener('keyup', e => { if (e.key === ' ')
                e.preventDefault(); }); // 브라우저 기본 토글(클릭) 막음
        ie.addEventListener('blur', () => { if (this.edit?.input === input) {
            if (this.commit())
                this.endEdit(false);
        } });
        return true;
    }
    commit() {
        const ed = this.edit;
        if (!ed)
            return true;
        if (CHECKISH.has(ed.kind))
            return true; // 누를 때마다 이미 저장
        const g = this.grid;
        let err;
        if (ed.kind === 'combo') {
            const v = ed.input.value === '' ? null : ed.input.value, old = g.ds.get(ed.row, ed.col);
            err = String(old ?? '') === String(v ?? '') ? null : g.editCellValue(ed.row, ed.col, v);
        }
        else
            err = g.editCellText(ed.row, ed.col, ed.input.value);
        if (err) {
            ed.input.classList.add('og-bad');
            ed.input.title = err;
            ed.input.setAttribute('aria-invalid', 'true');
            return false;
        }
        return true;
    }
    endEdit(refocus = true) { const ed = this.edit; this.edit = null; if (ed && CHECKISH.has(ed.kind))
        ed.input.tabIndex = -1; this.refresh(); if (refocus)
        this.focus(); }
    onKey(e) {
        const tg = e.target;
        if (tg.tagName === 'INPUT' || tg.tagName === 'SELECT')
            return;
        const n = this.items.length, L = this.lineH.length;
        const go = (i) => { e.preventDefault(); this.select(i); };
        // 위·아래 줄의 같은 자리 (짧은 줄이면 그 줄 끝)
        const lineMove = (d) => {
            const l = this.lineOf(this.cur), t = Math.max(0, Math.min(L - 1, l + d));
            if (t === l)
                return go(d > 0 ? n - 1 : 0);
            const k = this.cur - this.lineStart[l];
            go(Math.min(this.lineStart[t] + k, this.lineStart[t + 1] - 1));
        };
        const page = Math.max(1, Math.floor((this.el.clientHeight || 400) / (this.cardH + GAP)));
        const onGroup = (this.items[this.cur] ?? 0) < 0;
        switch (e.key) {
            case 'ArrowRight': return go(this.cur + 1);
            case 'ArrowLeft': return go(this.cur - 1);
            case 'ArrowDown': return lineMove(1);
            case 'ArrowUp': return lineMove(-1);
            case 'PageDown': return lineMove(page);
            case 'PageUp': return lineMove(-page);
            case 'Home': return go(0);
            case 'End': return go(n - 1);
            case ' ':
                if (onGroup) {
                    e.preventDefault();
                    this.toggleGroupAt(this.cur);
                }
                return;
            case 'Enter':
            case 'F2':
                e.preventDefault();
                if (onGroup)
                    this.toggleGroupAt(this.cur);
                else
                    this.beginEdit();
                return;
        }
    }
}
